
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { PlaylistCard } from '@/components/collections/PlaylistCard';
import { CollectionsFilter } from '@/components/collections/CollectionsFilter';
import { useCollectionsFilters } from '@/hooks/use-collections-filters';
import { CollectionsHeader } from '@/components/collections/CollectionsHeader';
import { CollectionsTabs } from '@/components/collections/CollectionsTabs';
import { useAuth } from '@/hooks/useAuth';
import { EnhancedSEO } from '@/components/seo/EnhancedSEO';

const Collections = () => {
  const { isAuthenticated } = useAuth();
  const {
    filters,
    rawFilters,
    setNameFilter,
    setCityFilter,
    setCountryFilter,
    setSortOption,
    handleApplyFilters,
    handleClearFilters,
    hasUnappliedChanges
  } = useCollectionsFilters();

  const { data: collections, isLoading } = useQuery({
    queryKey: ['collections', filters],
    queryFn: async () => {
      let query = supabase
        .from('destination_playlists')
        .select(`
          *,
          created_by_user:profiles!destination_playlists_created_by_user_id_fkey(
            username,
            avatar_url
          ),
          places_count:destination_playlist_items!destination_playlist_items_playlist_id_fkey(count)
        `)
        .eq('is_public', true);

      // Apply filters
      if (filters.nameFilter) {
        query = query.ilike('name', `%${filters.nameFilter}%`);
      }

      if (filters.cityFilter) {
        query = query.ilike('city', `%${filters.cityFilter}%`);
      }

      if (filters.countryFilter) {
        query = query.ilike('country', `%${filters.countryFilter}%`);
      }

      // Apply sorting
      switch (filters.sortOption) {
        case 'name':
          query = query.order('name');
          break;
        case 'popularity':
          query = query.order('followers_count', { ascending: false });
          break;
        case 'recent':
        default:
          query = query.order('created_at', { ascending: false });
          break;
      }

      const { data, error } = await query;
      if (error) throw error;
      return data;
    },
  });

  return (
    <>
      <EnhancedSEO 
        title="Travel Collections - Curated Destination Lists"
        description="Discover and create curated travel collections. Find the best destinations, restaurants, and attractions organized by fellow travelers."
      />
      
      <div className="container mx-auto px-4 py-8">
        <CollectionsHeader isAuthenticated={isAuthenticated} />
        
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8 mt-8">
          <aside className="lg:col-span-1">
            <CollectionsFilter
              nameFilter={rawFilters.nameFilter}
              cityFilter={rawFilters.cityFilter}
              countryFilter={rawFilters.countryFilter}
              sortOption={rawFilters.sortOption}
              onNameFilterChange={setNameFilter}
              onCityFilterChange={setCityFilter}
              onCountryFilterChange={setCountryFilter}
              onSortOptionChange={setSortOption}
              onApplyFilters={handleApplyFilters}
              onClearFilters={handleClearFilters}
              hasUnappliedChanges={hasUnappliedChanges}
            />
          </aside>
          
          <main className="lg:col-span-3">
            <CollectionsTabs 
              activeTab="all"
              onTabChange={() => {}}
              playlists={collections || []}
              popularPlaylists={[]}
              userPlaylists={[]}
              followedPlaylists={[]}
              isLoading={isLoading}
              popularLoading={false}
              userPlaylistsLoading={false}
              followedPlaylistsLoading={false}
              isAuthenticated={isAuthenticated}
            />
          </main>
        </div>
      </div>
    </>
  );
};

export default Collections;
